import { Pagination } from 'antd';
import { ComponentType, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { useLocation, useNavigate } from 'react-router';
import { SORT_DIRECTION } from '../../interfaces/enumerables';
import { GetPaginationData, SortBy, ValueLabel } from '../../interfaces/forms.interface';
import { useHasActivePlan } from '../../services/utils/useHasActivePlan';
import { images } from '../../theme/assets/img';
import { DataPlaceholder } from '../atoms/DataPlaceholder';
import { Loading } from '../atoms/Loading';
import { Filters } from './Filters';

interface Props<T, Tf extends { [key: string]: SortBy }> {
  useDataHook: (
    page: number,
    sortByValue: any,
    sortDirection: SORT_DIRECTION,
    search: string,
    type?: any,
  ) => UseQueryResult<GetPaginationData<T>, Error>;
  ItemCardData: ComponentType<{ index: number; key: number; data: T }>;
  sortByFilter: Tf;
  sortByDefaultKey: keyof Tf;
  sortDirectionValue?: SORT_DIRECTION;
  hideCreateButton?: boolean;
  hideSearchButton?: boolean;
  hideFiltersSection?: boolean;
  staticParam?: { [key: string]: never };
  initSearchValue?: string;
  typeFilter?: Record<string, ValueLabel>;
  headerClass?: string;
  noDataMsg?: string;
  headerSlot?: React.ReactNode;
}

export const EntityPaginationData = <T extends { id: number | string }, Tf extends { [key: string]: SortBy }>({
  useDataHook,
  ItemCardData,
  sortByFilter,
  sortByDefaultKey,
  sortDirectionValue = SORT_DIRECTION.ASC,
  hideCreateButton,
  hideSearchButton,
  hideFiltersSection,
  initSearchValue,
  typeFilter,
  headerClass = '',
  noDataMsg = 'SHARED.NO_DATA',
  headerSlot,
}: Props<T, Tf>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialSortBy = sortByFilter[searchParams?.get('sortBy') || ''] || sortByFilter[sortByDefaultKey];
  const initialSortDirection = (searchParams.get('sortDirection') as SORT_DIRECTION) || sortDirectionValue;
  const initialTypeFilter = typeFilter?.[searchParams?.get('type') || ''] || typeFilter?.['ALL'];
  const initialSearch = searchParams.get('search') || initSearchValue || '';
  const initialPage = Number(searchParams.get('page') || 1);

  const [page, setPage] = useState<number>(initialPage);
  const [sortBy, setSortBy] = useState<SortBy>(initialSortBy);
  const [type, setType] = useState<ValueLabel | undefined>(initialTypeFilter);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTION>(initialSortDirection);
  const [search, setSearch] = useState<string>(initialSearch);
  const { isLoading, data, isError, refetch } = useDataHook(page, sortBy.value, sortDirection, search, type?.value);

  const hasActivePlans = useHasActivePlan();

  // Sync page state with URL params
  useEffect(() => {
    const currentPage = Number(searchParams.get('page') || '1');
    setPage(currentPage);
  }, [searchParams]);

  const updateURL = useCallback(
    (newPage: number) => {
      searchParams.set('page', newPage.toString());
      navigate({ search: searchParams.toString() });
    },
    [navigate, searchParams],
  );

  const showTotal = (total: number) => {
    return t('SHARED.TOTAL_PAGINATION', { total });
  };

  const changePage = useCallback(
    (newPage: number) => {
      setPage(newPage);
      updateURL(newPage);
    },
    [updateURL],
  );

  const renderList = () => {
    if (data && data?.data?.length > 0) {
      return data.data.map((item, index) => {
        return <ItemCardData index={index} key={index} data={item} />;
      });
    }
  };

  const getFilters = useCallback(() => {
    if (!hideFiltersSection) {
      return (
        <div className="entity-pagination-filters pagination-layout">
          <div className="list-padding-horizontal">
            <Filters
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              search={search}
              setSearch={setSearch}
              sortByFilterData={sortByFilter}
              hideCreateButton={hideCreateButton || !hasActivePlans}
              hideSearchButton={hideSearchButton}
              type={type}
              setType={setType}
              typeFilterData={typeFilter}
            />
          </div>
        </div>
      );
    }
  }, [
    hasActivePlans,
    hideCreateButton,
    hideFiltersSection,
    hideSearchButton,
    search,
    sortBy,
    sortByFilter,
    sortDirection,
    type,
    typeFilter,
  ]);

  return (
    <div className={`entity-pagination-wrapper ${headerClass}`}>
      {headerSlot}
      {getFilters()}
      {data && data?.data?.length > 0 && (
        <div className="entity-pagination-content pagination-layout">
          <div className="entity-pagination-list list-padding-horizontal list-items-itemOfList padding-t-10">
            {renderList()}
          </div>
          <div className="entity-pagination-footer pagination-layout padding-r-30">
            <Pagination
              size="small"
              current={page}
              total={data?.total || 0}
              defaultPageSize={20}
              showSizeChanger={false}
              showTotal={showTotal}
              onChange={changePage}
              hideOnSinglePage={true}
            />
          </div>
        </div>
      )}
      {isError && (
        <DataPlaceholder imgSource={images.somethingWentWrong} msg={'SHARED.SOMETHING_WENT_WRONG'} refetch={refetch} />
      )}
      {data?.data?.length === 0 && <DataPlaceholder imgSource={images.noData} msg={noDataMsg} />}
      {isLoading && <Loading />}
    </div>
  );
};
